import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { db } from "@lib/firebase";
import {
  collection,
  query,
  onSnapshot,
  where,
  limit,
  startAfter,
  orderBy,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClock,
  faCheckCircle,
  faSearch,
  faSortUp,
  faSortDown,
  faChevronDown,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { InputField } from "@cakrawalajakarta/shared-components";

const PAGE_SIZE = 50;

const SkeletonRow = React.memo(() => (
  <tr className="border-b border-gray-200">
    <td className="px-4 py-4">
      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
    </td>
    <td className="px-4 py-4 hidden md:table-cell">
      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
    </td>
    <td className="px-4 py-4">
      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
    </td>
    <td className="px-4 py-4">
      <div className="h-4 bg-gray-200 rounded animate-pulse w-4"></div>
    </td>
  </tr>
));

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const CategoryButton = React.memo(({ category, selectedCategory, onClick }) => (
  <button
    onClick={() => onClick(category.distance)}
    className={`px-4 py-2 sm:px-4 sm:py-2 rounded-xl text-xs sm:text-sm font-medium transition-colors ${
      selectedCategory === category.distance
        ? "bg-lime-600 text-white"
        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
    }`}
    aria-label={`Filter peserta berdasarkan kategori ${category.displayName}`}
  >
    {category.displayName}
  </button>
));

export const EntryListPage = React.memo(() => {
  const [state, setState] = useState({
    participants: [],
    allParticipants: [],
    filteredParticipants: [],
    fullFilteredList: [],
    searchTerm: "",
    eventTitle: "JOTR",
    selectedCategory: "all",
    categories: [{ distance: "all", displayName: "ALL", description: "ALL" }],
    loading: true,
    loadingMore: false,
    error: null,
    collectionName: "pendaftaranJOTR",
    lastVisible: null,
    totalParticipants: 0,
    totalFiltered: 0,
    sortGenderDirection: null,
    hasMore: true,
    searchPageIndex: 0,
    categoriesLoaded: false,
    selectedGroup: null,
  });
  const isMounted = useRef(true);
  const unsubscribeRef = useRef({
    participants: null,
    total: null,
  });
  const searchPageIndexRef = useRef(0);

  const debouncedSearchTerm = useDebounce(state.searchTerm, 300);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      Object.values(unsubscribeRef.current).forEach((unsubscribe) => {
        if (unsubscribe) unsubscribe();
      });
    };
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const eventRef = doc(db, "semuaKegiatan", "JOTR");
      const eventDoc = await getDoc(eventRef);
      if (eventDoc.exists()) {
        const rawCategories = eventDoc.data().categories || [];

        // Combine categories with distance 20
        const combined20K = rawCategories
          .filter((cat) => cat.distance === 20)
          .map((cat) => cat.categoryName);
        // Map categories, setting displayName for buttons
        const mappedCategories = rawCategories.map((cat) => ({
          distance:
            cat.categoryName === "Fun Trail (7K)"
              ? "7-fun"
              : cat.distance.toString().trim(),
          displayName:
            cat.categoryName === "Fun Trail (7K)"
              ? "FUN"
              : cat.distance === 20
              ? "20 KM"
              : `${cat.distance} KM`,
          description: cat.description || `${cat.distance} KM`,
          categoryName: cat.categoryName || `${cat.distance} KM`,
        }));
        // Sort categories: all first, then 7, 20, 40, 80, then Fun Trail
        const sortedCategories = [
          { distance: "all", displayName: "ALL", description: "ALL" },
          ...mappedCategories.filter((cat) => cat.distance === "7"), // Vertical (7K)
          ...(combined20K.length > 0
            ? [
                {
                  distance: "20",
                  displayName: "20 KM",
                  description: "Classic Distance & Mix Group (20K)",
                  categoryName: combined20K,
                },
              ]
            : []),
          ...mappedCategories.filter((cat) => cat.distance === "40"), // Short Distance (40K)
          ...mappedCategories.filter((cat) => cat.distance === "80"), // Long Distance (80K)
          ...mappedCategories.filter((cat) => cat.distance === "7-fun"), // Fun Trail (7K)
        ];

        if (isMounted.current) {
          setState((prev) => ({
            ...prev,
            categories: sortedCategories,
            categoriesLoaded: true,
            loading: true,
          }));
        }
      } else {
        throw new Error("Dokumen JOTR tidak ditemukan di semuaKegiatan");
      }
    } catch (err) {
      if (isMounted.current) {
        setState((prev) => ({
          ...prev,
          error: `Gagal memuat kategori JOTR: ${err.message}`,
          loading: false,
        }));
      }
    }
  }, []);

  const mapCategoryToFirestore = useCallback(
    (distance) => {
      if (distance === "all") {
        return null;
      }
      const category = state.categories.find(
        (cat) => cat.distance === distance
      );
      let kategori;
      if (distance === "40") {
        // Handle both "Short Distance (40K)" and " Short Distance (40K)" for 40 KM
        kategori = ["Short Distance (40K)", " Short Distance (40K)"];
      } else {
        kategori = category
          ? Array.isArray(category.categoryName)
            ? category.categoryName
            : category.categoryName.trim()
          : null;
      }

      return kategori;
    },
    [state.categories]
  );

  const loadPage = useCallback(
    (collectionName, lastDoc, sortDirection, selectedDistance) => {
      if (!collectionName || !state.categoriesLoaded) {
        setState((prev) => ({ ...prev, loading: false, loadingMore: false }));
        return;
      }

      setState((prev) => ({
        ...prev,
        loading: !lastDoc,
        loadingMore: !!lastDoc,
        participants: lastDoc ? prev.participants : [],
        filteredParticipants: debouncedSearchTerm
          ? prev.filteredParticipants
          : [],
      }));

      const participantsCollection = collection(db, collectionName);
      const baseConstraints = [
        where("transaction_status", "in", ["settlement", "pending"]),
      ];
      const firestoreCategory = mapCategoryToFirestore(selectedDistance);
      if (firestoreCategory) {
        if (Array.isArray(firestoreCategory)) {
          baseConstraints.push(where("kategori", "in", firestoreCategory));
        } else {
          baseConstraints.push(where("kategori", "==", firestoreCategory));
        }
      }
      const orderConstraints = [];
      if (sortDirection) {
        orderConstraints.push(orderBy("gender", sortDirection));
      }
      orderConstraints.push(orderBy("transaction_status"));
      orderConstraints.push(orderBy("namaLengkap"));

      const qParticipants = lastDoc
        ? query(
            participantsCollection,
            ...baseConstraints,
            ...orderConstraints,
            startAfter(lastDoc),
            limit(PAGE_SIZE)
          )
        : query(
            participantsCollection,
            ...baseConstraints,
            ...orderConstraints,
            limit(PAGE_SIZE)
          );

      const unsubscribe = onSnapshot(
        qParticipants,
        (snapshot) => {
          if (!isMounted.current) return;

          if (snapshot.empty) {
            setState((prev) => ({
              ...prev,
              participants: debouncedSearchTerm ? prev.participants : [],
              filteredParticipants: debouncedSearchTerm
                ? prev.filteredParticipants
                : [],
              lastVisible: null,
              loading: false,
              loadingMore: false,
              hasMore: false,
            }));
            return;
          }

          const participantList = snapshot.docs.map((doc) => {
            const data = doc.data();
            // Include main account as the first member for Mix Group (20K)
            const mainAccountMember =
              data.kategori === "Mix Group (20K)" && data.namaLengkap
                ? {
                    namaDepan: data.namaLengkap.split(" ")[0] || "",
                    namaBelakang:
                      data.namaLengkap.split(" ").slice(1).join(" ") || "",
                    gender: data.gender || "Tidak diketahui",
                    bibName: data.bibName || "",
                  }
                : null;
            const participants = Array.isArray(data.participants)
              ? [mainAccountMember, ...data.participants].filter(Boolean)
              : mainAccountMember
              ? [mainAccountMember]
              : [];

            return {
              id: doc.id,
              nama:
                data.teamName && data.kategori === "Mix Group (20K)"
                  ? data.teamName
                  : data.namaLengkap || "Nama tidak tersedia",
              gender:
                data.kategori === "Mix Group (20K)"
                  ? "Mix"
                  : data.gender || "Tidak diketahui",
              kota: data.kota || data.alamat || "Tidak diketahui",
              statusPembayaran: data.transaction_status || "pending",
              category: data.kategori || "Tidak diketahui",
              participants,
              docRef: doc,
            };
          });

          setState((prev) => {
            const updatedParticipants = lastDoc
              ? [...prev.participants, ...participantList]
              : participantList;
            return {
              ...prev,
              participants: updatedParticipants,
              filteredParticipants: debouncedSearchTerm
                ? prev.filteredParticipants
                : updatedParticipants,
              lastVisible:
                snapshot.docs.length > 0
                  ? snapshot.docs[snapshot.docs.length - 1]
                  : null,
              loading: false,
              loadingMore: false,
              totalFiltered: debouncedSearchTerm
                ? prev.totalFiltered
                : prev.totalParticipants,
              hasMore: snapshot.size === PAGE_SIZE,
            };
          });
        },
        (err) => {
          if (!isMounted.current) return;
          setState((prev) => ({
            ...prev,
            error: `Gagal memuat data peserta: ${err.message}. Silakan periksa indeks Firestore atau hubungi admin.`,
            loading: false,
            loadingMore: false,
          }));
        }
      );

      unsubscribeRef.current.participants = unsubscribe;
    },
    [debouncedSearchTerm, state.categoriesLoaded, mapCategoryToFirestore]
  );

  const fetchAllParticipants = useCallback(
    async (collectionName, selectedDistance) => {
      if (!collectionName || !state.categoriesLoaded) return;

      try {
        const participantsCollection = collection(db, collectionName);
        const baseConstraints = [
          where("transaction_status", "in", ["settlement", "pending"]),
        ];
        const firestoreCategory = mapCategoryToFirestore(selectedDistance);
        if (firestoreCategory) {
          if (Array.isArray(firestoreCategory)) {
            baseConstraints.push(where("kategori", "in", firestoreCategory));
          } else {
            baseConstraints.push(where("kategori", "==", firestoreCategory));
          }
        }
        const q = query(participantsCollection, ...baseConstraints);

        const snapshot = await getDocs(q);

        const allParticipants = snapshot.docs.map((doc) => {
          const data = doc.data();
          // Include main account as the first member for Mix Group (20K)
          const mainAccountMember =
            data.kategori === "Mix Group (20K)" && data.namaLengkap
              ? {
                  namaDepan: data.namaLengkap.split(" ")[0] || "",
                  namaBelakang:
                    data.namaLengkap.split(" ").slice(1).join(" ") || "",
                  gender: data.gender || "Tidak diketahui",
                  bibName: data.bibName || "",
                }
              : null;
          const participants = Array.isArray(data.participants)
            ? [mainAccountMember, ...data.participants].filter(Boolean)
            : mainAccountMember
            ? [mainAccountMember]
            : [];

          return {
            id: doc.id,
            nama:
              data.teamName && data.kategori === "Mix Group (20K)"
                ? data.teamName
                : data.namaLengkap || "Nama tidak tersedia",
            gender:
              data.kategori === "Mix Group (20K)"
                ? "Mix"
                : data.gender || "Tidak diketahui",
            kota: data.kota || data.alamat || "Tidak diketahui",
            statusPembayaran: data.transaction_status || "pending",
            category: data.kategori || "Tidak diketahui",
            participants,
            docRef: doc,
          };
        });
        if (isMounted.current) {
          setState((prev) => ({
            ...prev,
            allParticipants,
            totalParticipants: snapshot.size,
            totalFiltered: debouncedSearchTerm
              ? prev.totalFiltered
              : snapshot.size,
          }));
        }
      } catch (err) {
        if (isMounted.current) {
          setState((prev) => ({
            ...prev,
            error: `Gagal memuat semua peserta: ${err.message}. Silakan hubungi admin.`,
          }));
        }
      }
    },
    [debouncedSearchTerm, state.categoriesLoaded, mapCategoryToFirestore]
  );

  const handleCategoryChange = useCallback(
    (distance) => {
      if (distance === state.selectedCategory) return;
      setState((prev) => ({
        ...prev,
        selectedCategory: distance,
        participants: [],
        filteredParticipants: [],
        fullFilteredList: [],
        lastVisible: null,
        hasMore: true,
        searchTerm: "",
        totalFiltered: 0,
        selectedGroup: null,
      }));
      searchPageIndexRef.current = 0;
      if (state.collectionName && state.categoriesLoaded) {
        fetchAllParticipants(state.collectionName, distance);
        loadPage(
          state.collectionName,
          null,
          state.sortGenderDirection,
          distance
        );
      }
    },
    [
      fetchAllParticipants,
      loadPage,
      state.collectionName,
      state.sortGenderDirection,
      state.categoriesLoaded,
      state.selectedCategory,
    ]
  );

  const handleShowGroupMembers = useCallback((participant) => {
    setState((prev) => ({
      ...prev,
      selectedGroup: participant,
    }));
  }, []);

  const handleCloseModal = useCallback(() => {
    setState((prev) => ({
      ...prev,
      selectedGroup: null,
    }));
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    if (state.categoriesLoaded && state.collectionName) {
      fetchAllParticipants(state.collectionName, state.selectedCategory);
      loadPage(
        state.collectionName,
        null,
        state.sortGenderDirection,
        state.selectedCategory
      );
    }
  }, [
    state.categoriesLoaded,
    state.collectionName,
    state.selectedCategory,
    state.sortGenderDirection,
    fetchAllParticipants,
    loadPage,
  ]);

  useEffect(() => {
    if (!state.collectionName || !state.categoriesLoaded) return;
    const participantsCollection = collection(db, state.collectionName);
    const baseConstraints = [
      where("transaction_status", "in", ["settlement", "pending"]),
    ];
    const firestoreCategory = mapCategoryToFirestore(state.selectedCategory);
    if (firestoreCategory) {
      if (Array.isArray(firestoreCategory)) {
        baseConstraints.push(where("kategori", "in", firestoreCategory));
      } else {
        baseConstraints.push(where("kategori", "==", firestoreCategory));
      }
    }
    const q = query(participantsCollection, ...baseConstraints);
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        if (!isMounted.current) return;

        setState((prev) => ({
          ...prev,
          totalParticipants: snapshot.size,
          totalFiltered: debouncedSearchTerm
            ? prev.totalFiltered
            : snapshot.size,
        }));
      },
      (err) => {
        if (!isMounted.current) return;
      }
    );
    unsubscribeRef.current.total = unsubscribe;
    return () => unsubscribe();
  }, [
    state.collectionName,
    state.selectedCategory,
    debouncedSearchTerm,
    state.categoriesLoaded,
  ]);

  useEffect(() => {
    if (!state.collectionName || !state.categoriesLoaded) return;

    if (debouncedSearchTerm) {
      const filteredList = state.allParticipants.filter((p) => {
        const nama = p.nama || "";
        return nama
          .toLowerCase()
          .includes(debouncedSearchTerm.toLowerCase().trim());
      });

      setState((prev) => {
        const initialParticipants = filteredList.slice(0, PAGE_SIZE);
        if (
          JSON.stringify(prev.filteredParticipants) ===
            JSON.stringify(initialParticipants) &&
          prev.totalFiltered === filteredList.length &&
          prev.fullFilteredList.length === filteredList.length
        ) {
          return prev;
        }
        searchPageIndexRef.current = 0;
        return {
          ...prev,
          filteredParticipants: initialParticipants,
          fullFilteredList: filteredList,
          totalFiltered: filteredList.length,
          hasMore: filteredList.length > PAGE_SIZE,
          lastVisible: null,
          searchPageIndex: 0,
          selectedGroup: null,
        };
      });
    } else {
      setState((prev) => {
        const newParticipants =
          prev.participants.length > 0 ? prev.participants : [];
        const newLastVisible =
          prev.participants.length > 0 &&
          prev.participants[prev.participants.length - 1]?.docRef
            ? prev.participants[prev.participants.length - 1].docRef
            : null;
        if (
          JSON.stringify(prev.filteredParticipants) ===
            JSON.stringify(newParticipants) &&
          prev.totalFiltered === prev.totalParticipants &&
          prev.lastVisible === newLastVisible
        ) {
          return prev;
        }
        searchPageIndexRef.current = 0;
        return {
          ...prev,
          filteredParticipants: newParticipants,
          fullFilteredList: [],
          totalFiltered: prev.totalParticipants,
          hasMore: newParticipants.length < prev.totalParticipants,
          lastVisible: newLastVisible,
          searchPageIndex: 0,
          selectedGroup: null,
        };
      });
    }
  }, [
    debouncedSearchTerm,
    state.allParticipants,
    state.collectionName,
    state.participants,
    state.totalParticipants,
    state.selectedCategory,
    state.categoriesLoaded,
  ]);

  const loadMore = useCallback(() => {
    if (debouncedSearchTerm) {
      if (!state.fullFilteredList.length) return;
      setState((prev) => {
        const currentIndex = searchPageIndexRef.current + 1;
        const startIndex = currentIndex * PAGE_SIZE;
        const additionalList = prev.fullFilteredList.slice(
          startIndex,
          startIndex + PAGE_SIZE
        );
        if (!additionalList.length) return { ...prev, hasMore: false };
        searchPageIndexRef.current = currentIndex;
        return {
          ...prev,
          filteredParticipants: [
            ...prev.filteredParticipants,
            ...additionalList,
          ],
          hasMore: startIndex + PAGE_SIZE < prev.fullFilteredList.length,
          searchPageIndex: currentIndex,
          lastVisible: null,
        };
      });
    } else {
      if (state.loading || state.loadingMore || !state.hasMore) return;
      let nextLastVisible = state.lastVisible;
      if (!nextLastVisible && state.participants.length > 0) {
        nextLastVisible =
          state.participants[state.participants.length - 1]?.docRef || null;
      }
      if (!nextLastVisible) return;
      loadPage(
        state.collectionName,
        nextLastVisible,
        state.sortGenderDirection,
        state.selectedCategory
      );
    }
  }, [
    debouncedSearchTerm,
    state.fullFilteredList,
    state.lastVisible,
    state.loading,
    state.loadingMore,
    state.collectionName,
    state.sortGenderDirection,
    state.hasMore,
    state.participants,
    state.selectedCategory,
  ]);

  const handleSearchChange = useCallback((e) => {
    const searchValue = e.target.value || "";
    setState((prev) => {
      searchPageIndexRef.current = 0;
      return {
        ...prev,
        searchTerm: searchValue,
        lastVisible: searchValue ? null : prev.lastVisible,
        hasMore: true,
        searchPageIndex: 0,
        selectedGroup: null,
      };
    });
  }, []);

  const handleGenderSortToggle = useCallback(() => {
    const newDirection = state.sortGenderDirection === "desc" ? "asc" : "desc";
    if (!state.collectionName) return;
    setState((prev) => {
      searchPageIndexRef.current = 0;
      return {
        ...prev,
        sortGenderDirection: newDirection,
        lastVisible: null,
        hasMore: true,
        searchPageIndex: 0,
        selectedGroup: null,
      };
    });
    loadPage(state.collectionName, null, newDirection, state.selectedCategory);
  }, [
    state.sortGenderDirection,
    state.collectionName,
    loadPage,
    state.selectedCategory,
  ]);

  const rangeText = `${state.filteredParticipants.length} dari ${
    debouncedSearchTerm ? state.totalFiltered : state.totalParticipants
  } Peserta`;

  if (state.error) {
    return (
      <section className="w-full min-h-screen flex items-center justify-center flex-col bg-white text-center px-4">
        <p className="text-lg text-gray-600 mb-4">{state.error}</p>
        <Link to="/kegiatan" className="text-lime-600 underline text-base">
          Kembali ke Daftar Kegiatan
        </Link>
      </section>
    );
  }

  return (
    <section className="w-full min-h-screen py-20 text-center text-lime-600 flex-col justify-center relative bg-white">
      <div className="relative z-1 container mx-auto px-4">
        <motion.div
          className="mb-6 flex flex-col gap-4 max-w-6xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          viewport={{ once: true }}
        >
          <h3 className="text-lg md:text-xl font-black uppercase text-lime-600 text-center block md:hidden">
            JAKARTA OPEN TRAIL RUN
          </h3>
          <div className="w-full max-w-md mx-auto md:ml-0">
            <InputField
              name="search"
              type="text"
              value={state.searchTerm}
              onChange={handleSearchChange}
              placeholder="Cari berdasarkan nama..."
              aria-label="Cari peserta berdasarkan nama"
              inputClassName="input w-full p-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-lime-600 text-slate-800 text-sm"
              startIcon={
                <FontAwesomeIcon icon={faSearch} className="text-gray-400" />
              }
            />
          </div>

          <div className="flex flex-col md:flex-row-reverse md:items-center md:justify-between gap-4">
            <h3 className="text-lg md:text-xl font-black uppercase text-lime-600 md:text-right hidden md:block">
              JAKARTA OPEN TRAIL RUN
            </h3>
            {state.categories.length > 1 && (
              <div className="flex flex-wrap gap-2">
                {state.categories.map((category) => (
                  <CategoryButton
                    key={category.distance}
                    category={category}
                    selectedCategory={state.selectedCategory}
                    onClick={handleCategoryChange}
                  />
                ))}
              </div>
            )}
          </div>
        </motion.div>
        <motion.div
          className="bg-white rounded-2xl border border-gray-200 text-left relative overflow-hidden max-w-6xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          viewport={{ once: true }}
        >
          <div className="overflow-x-auto">
            <table className="w-full table-auto divide-y divide-gray-200">
              <thead>
                <tr className="bg-gray-100">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                    Nama
                  </th>
                  <th
                    className="px-4 py-3 text-left md:text-center text-xs font-medium text-gray-600 uppercase tracking-wider cursor-pointer hover:text-lime-600 hidden md:table-cell"
                    onClick={handleGenderSortToggle}
                    aria-label="Urutkan berdasarkan gender"
                  >
                    <span className="flex items-center md:justify-center">
                      Gender
                      {state.sortGenderDirection && (
                        <FontAwesomeIcon
                          icon={
                            state.sortGenderDirection === "asc"
                              ? faSortUp
                              : faSortDown
                          }
                          className="ml-1 text-gray-500"
                        />
                      )}
                    </span>
                  </th>
                  <th className="px-4 py-3 text-left md:text-center text-xs font-medium text-gray-600 uppercase tracking-wider">
                    Kota
                  </th>
                  <th className="px-4 py-3 text-left md:text-center text-xs font-medium text-gray-600 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {state.loading ? (
                  <SkeletonRow />
                ) : state.filteredParticipants.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-4 py-4 text-center text-sm text-gray-600"
                    >
                      {state.searchTerm
                        ? "Tidak ada peserta yang cocok dengan pencarian."
                        : state.selectedCategory !== "all"
                        ? "Belum ada peserta terdaftar untuk kategori ini."
                        : "Belum ada peserta yang terdaftar."}
                    </td>
                  </tr>
                ) : (
                  state.filteredParticipants.map((participant) => (
                    <tr
                      key={participant.id}
                      className="border-b border-gray-200"
                    >
                      <td
                        className={`px-4 py-4 text-sm whitespace-nowrap md:whitespace-normal ${
                          participant.gender === "Perempuan"
                            ? "text-lime-600"
                            : "text-gray-600"
                        }`}
                      >
                        {participant.nama
                          ? participant.nama.toUpperCase()
                          : "-"}
                        {participant.category === "Mix Group (20K)" && (
                          <FontAwesomeIcon
                            icon={faUsers}
                            className="ml-2 text-lime-600 cursor-pointer"
                            title="Lihat anggota grup"
                            onClick={() => handleShowGroupMembers(participant)}
                          />
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600 hidden md:table-cell md:text-center">
                        {participant.gender || "-"}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600 md:text-center">
                        {participant.kota || "-"}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        {participant.statusPembayaran === "settlement" ? (
                          <FontAwesomeIcon
                            icon={faCheckCircle}
                            className="text-lime-600 mx-auto block"
                            title="Settled"
                          />
                        ) : (
                          <FontAwesomeIcon
                            icon={faClock}
                            className="text-yellow-500 mx-auto block"
                            title="Pending"
                          />
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end items-center px-4 py-2">
            <span className="text-sm text-gray-600 mr-4">{rangeText}</span>
            <button
              onClick={loadMore}
              disabled={state.loading || state.loadingMore || !state.hasMore}
              className={`px-3 py-1 rounded-lg transition-colors duration-200 flex items-center gap-1
                ${
                  state.loading || state.loadingMore || !state.hasMore
                    ? "bg-gray-200 text-gray-600 cursor-not-allowed"
                    : "bg-lime-600 text-white hover:bg-lime-700"
                }`}
              aria-label={
                state.hasMore
                  ? "Muat lebih banyak peserta"
                  : "Akhir daftar peserta"
              }
            >
              <span>{state.hasMore ? "Lihat lebih" : "Akhir peserta"}</span>
              <FontAwesomeIcon icon={faChevronDown} />
            </button>
          </div>
        </motion.div>
      </div>

      {state.selectedGroup && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={handleCloseModal}
        >
          <div
            className="bg-white rounded-lg py-6 px-0 max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-medium  text-lime-600 mb-4 px-4 text-center">
              Anggota Grup: {state.selectedGroup.nama}
            </h3>
            {state.selectedGroup.participants.length > 0 ? (
              <table className="w-full table-auto border-none">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-4 py-2 text-xs font-medium text-gray-600 uppercase tracking-wider text-center">
                      Nama
                    </th>
                    <th className="px-4 py-2 text-xs font-medium text-gray-600 uppercase tracking-wider text-center">
                      Gender
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {state.selectedGroup.participants.map(
                    (member, index, array) => (
                      <tr
                        key={`${member.namaDepan}-${member.namaBelakang}-${index}`}
                        className={
                          index === array.length - 1
                            ? ""
                            : "border-b border-gray-200"
                        }
                      >
                        <td className="px-4 py-2 text-sm text-gray-600 text-center">
                          {`${member.namaDepan || ""} ${
                            member.namaBelakang || ""
                          }`.trim() || "Nama tidak tersedia"}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-600 text-center">
                          {member.gender || "Tidak diketahui"}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            ) : (
              <p className="text-sm text-gray-600 px-4 text-center">
                Tidak ada anggota grup yang tersedia. Data mungkin tidak
                lengkap.
              </p>
            )}
          </div>
        </div>
      )}
    </section>
  );
});
